import React, { useState, useEffect } from 'react';
import config, { authFetch } from '../config';
import './ReviewsList.css';

const ReviewsList = ({ user, onBackToUpload, onSelectReview, onCreateReview, onLogout }) => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [creating, setCreating] = useState(false);

  const handleDeleteReview = async (e, reviewId) => {
    e.stopPropagation();
    if (!window.confirm('Delete this review? This cannot be undone.')) return;

    try {
      const response = await authFetch(`${config.API_URL}/api/reviews/${reviewId}`, {
        method: 'DELETE',
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to delete review');
      }
      setReviews((prev) => prev.filter((r) => r.id !== reviewId));
    } catch (err) {
      alert(err.message || 'Failed to delete review');
    }
  };

  useEffect(() => {
    loadReviews();
  }, []);

  const loadReviews = async () => {
    try {
      setLoading(true);
      const ownRes = await authFetch(`${config.API_URL}/api/reviews`);
      if (!ownRes.ok) throw new Error('Failed to load reviews');
      const ownData = await ownRes.json();
      setReviews(ownData.reviews || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateReview = async (e) => {
    e.preventDefault();
    if (!newTitle.trim()) return;

    try {
      setCreating(true);
      const response = await authFetch(`${config.API_URL}/api/reviews`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ title: newTitle.trim(), description: newDescription.trim() }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to create review');
      }

      const data = await response.json();
      setReviews([data.review, ...reviews]);
      setNewTitle('');
      setNewDescription('');
      setShowCreateForm(false);
    } catch (err) {
      setError(err.message);
    } finally {
      setCreating(false);
    }
  };

  // Review sharing has been removed (no share links for whole reviews).

  if (loading) {
    return (
    <div className="reviews-container reviews-container--loading">
        <div className="reviews-loading-inline">
          <div className="loading-spinner" />
          <div className="loading-text">Loading your reviews…</div>
        </div>
    </div>
    );
  }

  let visibleReviews = reviews;

  // Count total items in grid (including create/share cards)
  let totalItems = visibleReviews.length;
  if (!showCreateForm) {
    totalItems += 1; // create-review-card
  }

  // Determine grid class based on item count
  let gridClass = 'reviews-list';
  if (totalItems <= 2) {
    gridClass += ' reviews-list--full-width';
  } else {
    gridClass += ' reviews-list--three-columns';
  }

  return (
    <div className="reviews-container">
      <div className="reviews-header">
        <div>
          {onBackToUpload && (
            <button onClick={onBackToUpload} className="back-button">
              ← Back to Upload
            </button>
          )}
          <h1>My Reviews (Projects)</h1>
          <p className="user-info">Welcome, {user?.name || user?.email}</p>
        </div>
        <button onClick={onLogout} className="logout-button">
          Logout
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}

      {/* Single group: My reviews (Shared with me tab removed) */}

      {showCreateForm && (
        <form onSubmit={handleCreateReview} className="create-review-form">
          <input
            type="text"
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
            placeholder="Review title (required)"
            required
            disabled={creating}
            className="review-title-input"
          />
          <textarea
            value={newDescription}
            onChange={(e) => setNewDescription(e.target.value)}
            placeholder="Description (optional)"
            disabled={creating}
            className="review-description-input"
            rows="3"
          />
          <div className="create-review-actions">
            <button type="submit" disabled={creating || !newTitle.trim()} className="submit-button">
              {creating ? 'Creating...' : 'Create Review'}
            </button>
            <button
              type="button"
              onClick={() => {
                setShowCreateForm(false);
                setNewTitle('');
                setNewDescription('');
              }}
              disabled={creating}
              className="cancel-button"
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      <div className={gridClass}>
        {!showCreateForm && (
          <button
            type="button"
            className="create-review-card"
            onClick={() => setShowCreateForm(true)}
          >
            <div className="create-review-card-icon">＋</div>
            <div className="create-review-card-text">
              <h3>Create new review</h3>
              <p>Start a new project to organize your PDFs.</p>
            </div>
          </button>
        )}
        {/* Review sharing removed: no \"Share a review\" card in Shared with me tab */}
        {visibleReviews.length > 0 && visibleReviews.map((review) => (
            <div
              key={review.id}
              className="review-card"
              onClick={() => onSelectReview(review)}
            >
              <button
                type="button"
                className="review-delete-btn"
                onClick={(e) => handleDeleteReview(e, review.id)}
                aria-label="Delete review"
                title="Delete review"
              >
                ✕
              </button>
              <h3>{review.title}</h3>
              {review.description && <p className="review-description">{review.description}</p>}
              <p className="review-meta">
                Created {new Date(review.createdAt).toLocaleDateString()}
              </p>
            </div>
          ))}
      </div>
    </div>
  );
};

export default ReviewsList;
